import { Component } from 'react';
import { connect } from 'react-redux';
import { asField } from 'informed';
import { Icon } from 'components/common/icons';

const NumberStepper = connect((state) => ({
  messages: state.messages,
  theme: state.theme
}))(
  asField(
    class extends Component {
      handleBlur = (ev) => {
        const value = parseFloat(ev.target.value);
        this.updateValue({ value: isNaN(value) ? 0 : value });
      };

      handleChange = (ev) => {
        const value = parseInt(ev.target.value);
        if (!isNaN(value)) this.updateValue({ value: ev.target.value });
        else this.updateValue({ value: 0 });
      };

      round = (value) => {
        const { precision } = this.props;

        let lastIsDecimalMark = false;
        if (value.length && value[value.length - 1] === '.')
          lastIsDecimalMark = true;

        const isValid = parseFloat(value, 10);

        if (!isNaN(isValid)) {
          const newValue = precision
            ? +isValid.toFixed(precision)
            : precision === 0
            ? isValid
            : parseFloat(isValid.toFixed(0), 10);
          return lastIsDecimalMark ? `${newValue}.` : newValue;
        }

        return 0;
      };

      validateMinMax = (value) => {
        const { min, max } = this.props;
        if (!isNaN(max) && value > max) return max;
        if (!isNaN(min) && value < min) return min;
        return value;
      };

      handleDecrement = () => {
        this.updateValue({ stepValue: this.props.stepValue * -1 });
      };

      handleDecrementFast = () => {
        this.updateValue({ stepValue: this.props.stepValue * -1 });
        this.speedUpdateTimeout = setTimeout(this.handleDecrementFast, 50);
      };

      handleDecrementMouseDown = () => {
        this.speedUpdateTimeout = setTimeout(this.handleDecrementFast, 500);
      };

      handleDecrementMouseUp = () => {
        clearTimeout(this.speedUpdateTimeout);
      };

      handleIncrement = () => {
        this.updateValue({ stepValue: this.props.stepValue });
      };

      handleIncrementFast = () => {
        this.updateValue({ stepValue: this.props.stepValue });
        this.speedUpdateTimeout = setTimeout(this.handleIncrementFast, 50);
      };

      handleIncrementMouseDown = () => {
        this.speedUpdateTimeout = setTimeout(this.handleIncrementFast, 500);
      };

      handleIncrementMouseUp = () => {
        clearTimeout(this.speedUpdateTimeout);
      };

      handleKeyDown = (event) => {
        if (event.key === 'ArrowUp') {
          this.handleIncrement();
        } else if (event.key === 'ArrowDown') {
          this.handleDecrement();
        } else if (event.key === ',') {
          event.preventDefault();
          this.updateValue({ value: `${this.props.fieldState.value}.` });
        }
      };

      updateValue = ({ stepValue, value }) => {
        const { fieldApi, fieldState, onChange } = this.props;
        let newValue = stepValue ? fieldState.value + stepValue : value;
        newValue = this.round(newValue);
        newValue = this.validateMinMax(newValue);
        fieldApi.setValue(newValue);
        fieldApi.setTouched(true);
        if (onChange) onChange(newValue);
      };

      render() {
        const {
          ariaLabel,
          darkMode,
          disabled,
          fieldState,
          inputStyle,
          label,
          max,
          messages,
          min,
          style,
          theme
        } = this.props;

        let inputColor = theme.textColor;
        let inputBackgroundColor = '#fff';
        let inputBackgroundColorDisabled = theme.lightGreyLighter;
        let inputBorderColor = theme.lightGreyDark;
        let inputBorderColorActive = '#a1a8aa';
        let buttonColor = theme.textColorLighter;
        let buttonBackgroundColor = 'rgba(0, 0, 0, 0.1)';
        let buttonHoverBackgroundColor = 'rgba(0, 0, 0, 0.15)';
        let buttonOutlineColor = 'rgba(0, 0, 0, 0.15)';
        let labelColor = theme.textColorLight;
        if (darkMode) {
          inputColor = '#fff';
          inputBackgroundColor = 'rgba(0, 0, 0, 0.2)';
          inputBackgroundColorDisabled = 'rgba(255, 255, 255, 0.2)';
          inputBorderColor = 'rgba(255, 255, 255, 0.3)';
          inputBorderColorActive = 'rgba(255, 255, 255, 0.6)';
          buttonColor = '#333';
          buttonBackgroundColor = 'rgba(255, 255, 255, 0.5)';
          buttonHoverBackgroundColor = 'rgba(255, 255, 255, 0.4)';
          buttonOutlineColor = 'rgba(255, 255, 255, 0.2)';
          labelColor = '#fff';
        }

        return (
          <div className="stepper" style={style}>
            <div className="wrapper">
              <input
                className="input"
                disabled={disabled}
                max={max}
                min={min}
                onBlur={this.handleBlur}
                onChange={this.handleChange}
                onKeyDown={this.handleKeyDown}
                style={inputStyle}
                type="text"
                pattern="^\d*(\.\d+)?$"
                value={fieldState.value}
                {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
              />

              <button
                aria-label={messages.commonDecrement}
                className="button decrement focus-box-shadow"
                disabled={
                  disabled || (min !== undefined && fieldState.value <= min)
                }
                onClick={this.handleDecrement}
                onMouseDown={this.handleDecrementMouseDown}
                onMouseUp={this.handleDecrementMouseUp}
                onMouseOut={this.handleDecrementMouseUp}
                type="button"
              >
                <Icon icon="minus" />
              </button>

              <button
                aria-label={messages.commonIncrement}
                className="button increment focus-box-shadow"
                disabled={
                  disabled || (max !== undefined && fieldState.value >= max)
                }
                onClick={this.handleIncrement}
                onMouseDown={this.handleIncrementMouseDown}
                onMouseUp={this.handleIncrementMouseUp}
                onMouseOut={this.handleIncrementMouseUp}
                type="button"
              >
                <Icon icon="plus" />
              </button>
            </div>

            {label ? (
              <div className={`label ${disabled ? 'disabled' : ''}`}>
                {label}
              </div>
            ) : null}

            <style jsx>{`
              .stepper {
                align-items: center;
                display: flex;
                position: relative;
              }
              .wrapper {
                position: relative;
              }
              .input {
                background-color: ${inputBackgroundColor};
                border: 1px solid ${inputBorderColor};
                border-radius: ${theme.inputBorderRadius};
                color: ${inputColor};
                font-family: inherit;
                font-size: 1.5rem;
                outline: 0;
                padding: 6px 10px;
                padding-right: 60px;
                position: relative;
                text-align: flex-start;
                transition: border-color 0.2s;
                width: 120px;
                -moz-appearance: textfield;
              }
              .input:focus {
                border-color: ${inputBorderColorActive};
              }
              .input:disabled {
                background-color: ${inputBackgroundColorDisabled};
                color: ${inputColor};
                opacity: 0.7;
              }
              .input::-webkit-outer-spin-button,
              .input::-webkit-inner-spin-button {
                margin: 0;
                -webkit-appearance: none;
              }
              .button {
                background-color: ${buttonBackgroundColor};
                border: 0;
                border-radius: 6px;
                bottom: 4px;
                color: ${buttonColor};
                cursor: pointer;
                display: inline-block;
                font-family: inherit;
                font-size: 1rem;
                font-weight: 700;
                outline: 0;
                padding: 0;
                position: absolute;
                text-align: center;
                top: 4px;
                transition: background-color 0.3s;
                white-space: nowrap;
                width: 24px;
                height: 24px;
                z-index: 1;
              }
              .button:hover {
                background-color: ${buttonHoverBackgroundColor};
              }
              .button:focus {
                box-shadow: 0 0 0 5px ${buttonOutlineColor};
              }
              .button:disabled {
                opacity: 0.5;
              }
              .button:disabled:hover {
                background-color: ${buttonBackgroundColor};
                cursor: default;
              }
              .decrement {
                right: 31px;
              }
              .increment {
                right: 4px;
              }
              .label {
                color: ${labelColor};
                display: inline-block;
                font-size: 1.5rem;
                font-weight: 400;
                margin-left: 10px;
              }
              .label.disabled {
                opacity: 0.5;
              }
            `}</style>
          </div>
        );
      }
    }
  )
);

export default NumberStepper;
